import { useState, useRef, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChevronLeft, ChevronRight, Grid, ZoomIn, ZoomOut, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { getRequest } from '../services/apiService';
import ReusableDeleteDialog from '../common/ReusableDeleteDialog';

// Mapping of chart categories to API endpoints
const CHART_CATEGORIES = {
  0: 'hull_structure',
  1: 'hull_outfit',
  2: 'engineering',
  3: 'piping',
  4: 'electrical',
  5: 'weapon',
  6: 'dry_dock_trials',
  7: 'comp_inspection'
};

export default function OutfittingProgressDashboard({ onShipSelect }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showAllCharts, setShowAllCharts] = useState(true);
  const [globalDataView, setGlobalDataView] = useState('yearly');
  const [individualChartViews, setIndividualChartViews] = useState({});
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedShip, setSelectedShip] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingShips, setLoadingShips] = useState(false);
  const [chartData, setChartData] = useState({});
  const [loadingCharts, setLoadingCharts] = useState(false);
  const chartRefs = useRef({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chartToDelete, setChartToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
   const handleShipChange = (e) => {
    const shipId = parseInt(e.target.value);
    const ship = ships.find(s => s.id === shipId);
    setSelectedShip(ship || null);
    if (onShipSelect) {
      onShipSelect(ship || null);
    }
  };

  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoadingProjects(true);
        const response = await getRequest('master/projects/');
        setProjects(response.data);
        // Find and set project with ID 1 as default if it exists
        const defaultProject = response.data.find(project => project.id === 1);
        if (defaultProject) {
          setSelectedProject(defaultProject);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Fetch ships when project is selected
  useEffect(() => {
    const fetchShips = async () => {
      if (!selectedProject) return;

      try {
        setLoadingShips(true);
        setSelectedShip(null); // Reset selected ship when project changes
        const response = await getRequest(`master/projects/?parent_id=${selectedProject.id}`);
        setShips(response.data);

        // If the selected project is ID 1, try to set ship ID 12 as default
        if (selectedProject.id === 1) {
          const defaultShip = response.data.find(ship => ship.id === 12);
          if (defaultShip) {
            setSelectedShip(defaultShip);
          }
        }
      } catch (error) {
        console.error('Error fetching ships:', error);
      } finally {
        setLoadingShips(false);
      }
    };

    fetchShips();
  }, [selectedProject]);

  // Fetch chart data when ship is selected
  useEffect(() => {
    const fetchChartData = async () => {
      if (!selectedShip) return;

      try {
        setLoadingCharts(true);
        const newChartData = {};

        // Fetch data for each chart category
        for (const [chartId, category] of Object.entries(CHART_CATEGORIES)) {
          try {
            const response = await getRequest(
              `transaction/outfitting-graph/${category}/?ship=${selectedShip.id}`
            );
            newChartData[chartId] = {
              name: getChartName(chartId),
              data: formatChartData(response)
            };
          } catch (error) {
            console.error(`Error fetching data for ${category}:`, error);
            newChartData[chartId] = {
              name: getChartName(chartId),
              data: generateEmptyData()
            };
          }
        }

        setChartData(newChartData);
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setLoadingCharts(false);
      }
    };

    fetchChartData();
  }, [selectedShip]);

  // Format API data to match our chart structure
  const formatChartData = (apiData) => {
    return {
      yearly: apiData.yearly || [],
      monthly: apiData.monthly.map(item => ({
        ...item,
        month: `${item.month}-${item.year.slice(-2)}` // Format as Jan-25
      })) || []
    };
  };

  // Generate empty data when API fails
  const generateEmptyData = () => {
    return {
      yearly: [{ year: new Date().getFullYear().toString(), month: 'All', planned: 0, actual: 0 }],
      monthly: [{ year: new Date().getFullYear().toString(), month: 'Jan', planned: 0, actual: 0 }]
    };
  };

  // Get chart name based on ID
  const getChartName = (chartId) => {
    const names = [
      'HULL STRUCTURE',
      'HULL OUTFIT',
      'ENGINEERING',
      'PIPING',
      'ELECTRICAL',
      'WEAPON',
      'DRY DOCK & TRIALS',
      'INSPECTION'
    ];
    return names[chartId] || `Chart ${chartId}`;
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? 7 : prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev === 7 ? 0 : prev + 1));
  };

  const toggleView = () => {
    setShowAllCharts(!showAllCharts);
  };

  const handleChartClick = (chartId) => {
    if (showAllCharts) {
      setCurrentSlide(chartId);
      setShowAllCharts(false);
    }
  };

  // Zoom functions
  const zoomIn = (chartId) => {
    if (chartId !== undefined) {
      setIndividualChartViews(prev => ({
        ...prev,
        [chartId]: 'monthly'
      }));
    } else {
      setGlobalDataView('monthly');
    }
  };

  const zoomOut = (chartId) => {
    if (chartId !== undefined) {
      setIndividualChartViews(prev => ({
        ...prev,
        [chartId]: 'yearly'
      }));
    } else {
      setGlobalDataView('yearly');
    }
  };

  const getChartDataView = (chartId) => {
    if (!showAllCharts && individualChartViews[chartId]) {
      return individualChartViews[chartId];
    }
    return showAllCharts ? globalDataView : individualChartViews[chartId] || 'yearly';
  };

  const getXAxisKey = (dataView) => {
    return dataView === 'yearly' ? 'year' : 'month';
  };

  const getFilteredData = (data, dataView) => {
    if (!data) return [];
    if (dataView === 'yearly') {
      return data.yearly.filter(item => item.month === 'All');
    }
    return data.monthly;
  };

  // Download all charts as single PNG
  const downloadAllChartsPNG = async () => {
    try {
      ensureHtml2Canvas();

      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.backgroundColor = 'white';
      container.style.padding = '20px';
      container.style.width = '100%';
      document.body.appendChild(container);

      const title = document.createElement('h1');
      title.textContent = 'Outfitting Progress - All Charts';
      title.style.textAlign = 'center';
      title.style.marginBottom = '20px';
      title.style.fontSize = '24px';
      title.style.fontWeight = 'bold';
      container.appendChild(title);

      const grid = document.createElement('div');
      grid.style.display = 'grid';
      grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
      grid.style.gap = '20px';
      container.appendChild(grid);

      Object.entries(chartData).forEach(([chartId, chart]) => {
        const chartContainer = document.createElement('div');
        chartContainer.style.backgroundColor = 'white';
        chartContainer.style.padding = '10px';
        chartContainer.style.border = '1px solid #eee';
        chartContainer.style.borderRadius = '8px';

        const chartTitle = document.createElement('h3');
        chartTitle.textContent = chart.name;
        chartTitle.style.textAlign = 'center';
        chartTitle.style.marginBottom = '10px';
        chartTitle.style.fontWeight = 'bold';
        chartContainer.appendChild(chartTitle);

        const chartElement = chartRefs.current[`single-${chartId}`] ||
          chartRefs.current[`grid-${chartId}`];
        if (chartElement) {
          const svgElement = chartElement.querySelector('svg');
          if (svgElement) {
            const svgClone = svgElement.cloneNode(true);
            svgClone.style.width = '100%';
            svgClone.style.height = 'auto';
            chartContainer.appendChild(svgClone);
          }
        }

        grid.appendChild(chartContainer);
      });

      if (window.html2canvas) {
        const canvas = await window.html2canvas(container, {
          backgroundColor: 'white',
          scale: 2,
          scrollY: 0
        });

        canvas.toBlob((blob) => {
          if (!blob) throw new Error('Failed to create blob from canvas');

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'all-outfitting-progress-charts.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          document.body.removeChild(container);
        }, 'image/png');
      } else {
        throw new Error('html2canvas not loaded');
      }
    } catch (error) {
      console.error('Error exporting all charts as PNG:', error);
      alert('Failed to export all charts as PNG. Please try again.');
    }
  };

  // Download all data as single XLSX with multiple sheets
  const downloadAllDataXLSX = () => {
    try {
      const workbook = XLSX.utils.book_new();

      // Add summary sheet
      const summaryData = [
        ['Chart Name', 'Yearly Planned', 'Yearly Actual', 'Monthly Planned', 'Monthly Actual'],
        ...Object.entries(chartData).map(([chartId, chart]) => {
          const yearlyPlanned = chart.data.yearly.reduce((sum, item) => sum + item.planned, 0);
          const yearlyActual = chart.data.yearly.reduce((sum, item) => sum + item.actual, 0);
          const monthlyPlanned = chart.data.monthly.reduce((sum, item) => sum + item.planned, 0);
          const monthlyActual = chart.data.monthly.reduce((sum, item) => sum + item.actual, 0);

          return [
            chart.name,
            yearlyPlanned.toFixed(2),
            yearlyActual.toFixed(2),
            monthlyPlanned.toFixed(2),
            monthlyActual.toFixed(2)
          ];
        })
      ];

      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(summaryData),
        'Summary'
      );

      // Add sheets for each chart
      Object.entries(chartData).forEach(([chartId, chart]) => {
        // Yearly data sheet
        const yearlySheetName = `${chart.name} (Yearly)`;
        const yearlyData = [
          ['Year', 'Planned', 'Actual'],
          ...chart.data.yearly.map(item => [
            item.year,
            item.planned.toFixed(2),
            item.actual.toFixed(2)
          ])
        ];

        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.aoa_to_sheet(yearlyData),
          yearlySheetName.substring(0, 31)
        );

        // Monthly data sheet
        const monthlySheetName = `${chart.name} (Monthly)`;
        const monthlyData = [
          ['Year', 'Month', 'Planned', 'Actual'],
          ...chart.data.monthly.map(item => [
            item.year,
            item.month,
            item.planned.toFixed(2),
            item.actual.toFixed(2)
          ])
        ];

        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.aoa_to_sheet(monthlyData),
          monthlySheetName.substring(0, 31)
        );
      });

      // Generate and download the file
      XLSX.writeFile(workbook, 'all-outfitting-progress-data.xlsx');
    } catch (error) {
      console.error('Error exporting all data:', error);
      alert('Failed to export all data. Please try again.');
    }
  };

  // Download single chart data as CSV
  const downloadChartCSV = (data, title) => {
    try {
      const dataView = getChartDataView(data.id);
      const currentData = getFilteredData(data.data, dataView);
      const xKey = getXAxisKey(dataView);

      let header = '';
      let rows = '';

      if (dataView === 'yearly') {
        header = 'year,planned,actual\n';
        rows = currentData.map(item =>
          `${item.year},${item.planned.toFixed(2)},${item.actual.toFixed(2)}`
        ).join('\n');
      } else {
        header = 'year,month,planned,actual\n';
        rows = currentData.map(item =>
          `${item.year},${item.month},${item.planned.toFixed(2)},${item.actual.toFixed(2)}`
        ).join('\n');
      }

      const csvContent = header + rows;

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${title.toLowerCase().replace(/ /g, '-')}-${dataView}-data.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  // Download single chart as PNG
  const downloadChartPNG = async (chartId, title) => {
    try {
      const chartContainer = chartRefs.current[chartId];
      if (!chartContainer) return;

      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.padding = '10px';
      document.body.appendChild(tempDiv);

      const titleElement = document.createElement('h2');
      titleElement.style.textAlign = 'center';
      titleElement.style.fontFamily = 'Arial, sans-serif';
      titleElement.style.fontSize = '16px';
      titleElement.style.fontWeight = 'bold';
      titleElement.style.margin = '0 0 10px 0';
      titleElement.textContent = title;
      tempDiv.appendChild(titleElement);

      const svgElement = chartContainer.querySelector('svg');
      if (!svgElement) {
        document.body.removeChild(tempDiv);
        return;
      }

      const svgClone = svgElement.cloneNode(true);
      const bbox = svgElement.getBoundingClientRect();

      svgClone.setAttribute('width', bbox.width);
      svgClone.setAttribute('height', bbox.height);
      svgClone.style.maxWidth = '100%';

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('width', '100%');
      rect.setAttribute('height', '100%');
      rect.setAttribute('fill', 'white');
      svgClone.insertBefore(rect, svgClone.firstChild);

      tempDiv.appendChild(svgClone);

      if (window.html2canvas) {
        const canvas = await window.html2canvas(tempDiv, {
          backgroundColor: 'white',
          scale: 2,
        });

        canvas.toBlob((blob) => {
          if (!blob) throw new Error('Failed to create blob from canvas');

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${title.toLowerCase().replace(/ /g, '-')}-chart.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          document.body.removeChild(tempDiv);
        }, 'image/png');
      } else {
        throw new Error('html2canvas not loaded');
      }
    } catch (error) {
      console.error('Error exporting chart as PNG:', error);
      alert('Failed to export chart as PNG. Please try again.');
    }
  };

  const ensureHtml2Canvas = () => {
    if (!window.html2canvas && !window._html2canvasLoading) {
      window._html2canvasLoading = true;
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      script.onload = () => { window._html2canvasLoading = false; };
      script.onerror = () => { window._html2canvasLoading = false; };
      document.head.appendChild(script);
    }
  };

  useState(() => {
    ensureHtml2Canvas();
  }, []);

  const Chart = ({ data, title, chartId }) => {
    const dataView = getChartDataView(chartId);
    const currentData = getFilteredData(data, dataView);
    const xKey = getXAxisKey(dataView);

    

    return (
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center mb-1">
          <h3 className="font-semibold text-lg">{title}</h3>
          <div className="flex space-x-2">
            {!showAllCharts && (
              <>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => zoomIn(chartId)}
                    className={`p-1 rounded ${dataView === 'monthly' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
                    title="Zoom In (Monthly View)"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => zoomOut(chartId)}
                    className={`p-1 rounded ${dataView === 'yearly' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
                    title="Zoom Out (Yearly View)"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => downloadChartCSV({ id: chartId, data }, title)}
                  className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                  title="Download CSV"
                >
                  CSV
                </button>
                <button
                  onClick={() => downloadChartPNG(chartId, title)}
                  className="px-2 py-1 text-xs bg-green-100 hover:bg-green-200 rounded-md transition-colors"
                  title="Download PNG"
                >
                  PNG
                </button>
                <button
                  onClick={() => openDeleteDialog(chartId)}
                  className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                  title="Delete Chart Data"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
        <div className="flex-1" ref={el => chartRefs.current[chartId] = el}>
          {loadingCharts ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={currentData}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={xKey} />
                <YAxis />
                <Tooltip
                  formatter={(value, name, props) => {
                    let label = '';
                    if (dataView === 'monthly') {
                      label = `${props.payload.year} - `;
                    }
                    label += `${props.payload[xKey]}`;
                    return [value.toFixed(2), label];
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="planned"
                  name={`Cumm. ${title.toLowerCase()} planned`}
                  stroke="#8884d8"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  connectNulls={true}
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name={`Cumm. ${title.toLowerCase()} actual`}
                  stroke="#82ca9d"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  // Handler to open delete dialog
  const openDeleteDialog = (chartId) => {
    setChartToDelete(chartId);
    setShowDeleteModal(true);
  };

  // Handler to actually delete chart data (implement your delete logic here)
  const handleDeleteChart = async () => {
    setDeleteLoading(true);
    try {
      // TODO: Implement actual delete logic for chart data (API call or state update)
      // For now, just close the dialog
      setShowDeleteModal(false);
      setChartToDelete(null);
      // Optionally, refresh chart data here
    } catch (error) {
      // Handle error
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg shadow-lg max-w-full mx-auto">
      <div className="flex justify-between items-end mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Outfitting Progress</h2>
        <div className="flex items-end space-x-4">
          {/* Project Dropdown */}
          <div className="relative">
            <span className="text-sm text-gray-500 font-bold mr-1">Project:</span>
            <select
              className="bg-white border border-gray-600 px-4 py-2 rounded-md shadow-sm appearance-none pr-8"
              value={selectedProject?.id || ''}
              onChange={(e) => {
                const projectId = parseInt(e.target.value);
                const project = projects.find(p => p.id === projectId);
                setSelectedProject(project || null);
              }}
              disabled={loadingProjects}
            >
              <option value="">Select Project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>
          </div>

          {/* Ship Dropdown */}
          <div className="relative">
            <span className="text-sm text-gray-500 font-bold mr-1">Ship:</span>
            <select
              className="bg-white border border-gray-600 px-4 py-2 rounded-md shadow-sm appearance-none pr-8"
              value={selectedShip?.id || ''}
              onChange={handleShipChange}
              disabled={loadingShips || !selectedProject}
            >
              <option value="">Select Ship</option>
              {ships.map((ship) => (
                <option key={ship.id} value={ship.id}>
                  {ship.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>
          </div>

          <button
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
            onClick={toggleView}
            disabled={loadingCharts || !selectedShip}
          >
            <Grid className="w-4 h-4 mr-2" />
            {showAllCharts ? "Single Chart View" : "All Charts View"}
          </button>
          {showAllCharts && (
            <>
              <button
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                onClick={downloadAllChartsPNG}
                title="Download All Charts as PNG"
                disabled={loadingCharts || !selectedShip}
              >
                <Download className="w-4 h-4 mr-2" />
                All PNG
              </button>
              <button
                className="flex items-center bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition"
                onClick={downloadAllDataXLSX}
                title="Download All Data as Excel (Multiple Sheets)"
                disabled={loadingCharts || !selectedShip}
              >
                <Download className="w-4 h-4 mr-2" />
                All Data
              </button>
            </>
          )}
        </div>
      </div>

      {showAllCharts && (
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-2">
            <span className="font-medium">Zoom Level:</span>
            <button
              onClick={() => zoomOut()}
              className={`px-3 py-1 rounded ${globalDataView === 'yearly' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              disabled={loadingCharts || !selectedShip}
            >
              Yearly
            </button>
            <button
              onClick={() => zoomIn()}
              className={`px-3 py-1 rounded ${globalDataView === 'monthly' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              disabled={loadingCharts || !selectedShip}
            >
              Monthly
            </button>
          </div>
          <div className="text-sm text-gray-500">
            Click on any chart to view it in single view
          </div>
        </div>
      )}

      {!selectedShip ? (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <p className="text-gray-600">
            {loadingShips ? 'Loading ships...' : 'Please select a project and ship to view data'}
          </p>
        </div>
      ) : !showAllCharts ? (
        <div className="relative bg-white p-4 rounded-lg shadow h-96">
          {loadingCharts ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              <Chart
                data={chartData[currentSlide]?.data}
                title={chartData[currentSlide]?.name || getChartName(currentSlide)}
                chartId={`single-${currentSlide}`}
              />

              <div className="absolute inset-y-0 left-0 flex items-center">
                <button
                  onClick={handlePrevSlide}
                  className="bg-gray-200 p-2 rounded-full hover:bg-gray-300 transition"
                  disabled={loadingCharts}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              </div>

              <div className="absolute inset-y-0 right-0 flex items-center">
                <button
                  onClick={handleNextSlide}
                  className="bg-gray-200 p-2 rounded-full hover:bg-gray-300 transition"
                  disabled={loadingCharts}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>

              <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-2">
                {[0, 1, 2, 3, 4, 5, 6, 7].map((chartId) => (
                  <button
                    key={chartId}
                    className={`mx-1 h-2 w-8 rounded-full ${currentSlide === chartId ? "bg-blue-600" : "bg-gray-300"
                      }`}
                    onClick={() => setCurrentSlide(chartId)}
                    disabled={loadingCharts}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {loadingCharts ? (
            <div className="col-span-4 flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            Object.entries(chartData).map(([chartId, chart]) => (
              <div
                key={chartId}
                className="bg-white p-3 rounded-lg shadow h-72 cursor-pointer hover:shadow-md transition"
                onClick={() => handleChartClick(parseInt(chartId))}
              >
                <Chart
                  data={chart.data}
                  title={chart.name}
                  chartId={`grid-${chartId}`}
                />
              </div>
            ))
          )}
        </div>
      )}

      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDeleteChart}
        loading={deleteLoading}
        title="Confirm Deletion"
        description={chartToDelete !== null ? `Are you sure you want to delete data for chart: ${getChartName(chartToDelete)}? This action cannot be undone.` : 'Are you sure you want to delete this chart data?'}
        deleteLabel="Delete Chart Data"
      />
    </div>
  );
}